import React, { Fragment, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
/* MODELS */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildPreProdContentPath,
  buildReviewPath,
  EditorialReviewEntities,
  EditorialReviewModel,
  FirstContentEntity,
} from 'client/data/models/editorial-review';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { FeatureSpecsEntities, FeatureSpecsPaths, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { PageModel } from 'client/data/models/page';
import {
  buildInventoryCountByTypePath,
  InventoryAggregateEntities,
  InventoryAggregateModel,
} from 'client/data/models/inventory-aggregate';
/* UTILS */
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import {
  cloneParamsWithPubState,
  getBodyTypeBreadcrumbFromCategories,
} from 'site-modules/shared/utils/core-page/page-helpers';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getEditorialIcon } from 'site-modules/shared/utils/core-page/rating-and-ranking';
import { isCoreEtrActivationEnabled } from 'site-modules/shared/utils/core-page/core-etr-activation';
import { getOverallRating } from 'site-modules/shared/utils/editorial-helper';
import { getPublicationStateForMakeModel, isPreProd } from 'site-modules/shared/utils/publication-states';
import { isUnknownZip } from 'site-modules/shared/utils/location';
/* ADS */
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { NativeVideoMrect } from 'site-modules/shared/components/native-ad/video-mrect/video-mrect-native';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
/* TRACKING */
import { TrackingMap } from 'client/tracking/maps/core-page';
import { TrackingHandler } from 'client/tracking/handler';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
/* UTIL COMPONENTS */
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
/* SHARED COMPONENTS */
import { CoreBreadcrumbs } from 'site-modules/shared/components/core-breadcrumbs/core-breadcrumbs';
import { PriceCheckerPromo } from 'site-modules/core-page/components/price-checker-promo/price-checker-promo';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { AppointmentSurveyExperiment } from 'site-modules/core-page/components/appointment-survey/appointment-survey-experiment';
import { ClippedContainer } from 'site-modules/shared/components/clipped-container/clipped-container';
/* COMPONENTS */
import { VehicleQuestions } from 'site-modules/core-page/components/vehicle-questions/vehicle-questions';
import { NewCoreIntro } from 'site-modules/core-page/components/new-core-intro/new-core-intro';
import { Reliability } from 'site-modules/core-page/components/reliability/reliability';
import { VehicleComparisons } from 'site-modules/shared/components/core-page/vehicle-comparisons/vehicle-comparisons';
import { LatestVideoForGenerationModule } from 'site-modules/shared/components/video-module/video-module';
import { FeaturesHighlights } from 'site-modules/core-page/components/features-highlights/features-highlights';
import { ShoppingLinks } from 'site-modules/core-page/components/shopping-links/shopping-links';
import { ConsumerReviewsModular } from 'site-modules/shared/components/consumer-reviews-modular/consumer-reviews-modular';
import { DockedSrpLink } from 'site-modules/core-page/components/docked-srp-link/docked-srp-link';
import { PhotoGallery } from 'site-modules/core-page/components/photo-gallery/photo-gallery';
import {
  OtherReviewYears,
  OtherReviewYearsGraphql,
} from 'site-modules/core-page/components/other-years-core-7456/other-review-years';
import { RangeAndCost } from 'site-modules/core-page/components/range-and-cost/range-and-cost';
/* CONSTANTS */
import { DEALERLESS_OEM } from 'site-modules/shared/constants/oem';
import {
  CORE_DESKTOP_HEADER_CORE_7705,
  CORE_HEADER_ETR_ACTIVATION,
  CORE_HEADER_SRC_INLINE,
} from 'site-modules/core-page/constants/core-page';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';
/* CSS */
import 'site-modules/core-page/components/preprod-articles/preprod-articles.scss';
import './new-core.scss';
/* LAZY IMPORTS */
import lazyImports from './lazy-imports';

const { FaqSection, Safety } = lazyImports;

const CONTENT_TRACKING_DATA = {
  action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
  action_name: TrackingConstant.ACTION_SHOW_CONTENT,
  synpartner: TrackingConstant.SYNPARTNER,
  action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
  page_name: 'new_model_core',
  page_category: 'new_model_core',
};

function NewCoreUI({
  vehicle = null,
  editorialReview = null,
  firstContent = null,
  visitorLocation = {},
  params: propsParams,
  stylesFeatures = null,
  sharedTrimName = null,
  isMobile = false,
  inventoryCount = null,
  core7007 = 'ctrl',
  ads11455Ads11617Recipe = 'ctrl',
  isCore7661Enabled = false,
  isCore7747 = false,
  isAds12269Enabled = false,
  core7705 = false,
  coreEtrActivation,
}) {
  const isTracked = useRef({ edmundsTypeCategories: false, teaser: false, editorialRating: false });

  useEffect(() => {
    TrackingHandler.tryAddMap(TrackingMap);
  }, []);

  const styleAttributes = get(
    stylesFeatures?.find(({ trim }) => sharedTrimName === trim) || stylesFeatures?.[0],
    'styleAttributes'
  );
  const isElectric = !!styleAttributes?.electric;
  const isPluginHybrid = !!styleAttributes?.pluginElectric;
  const isTruck = !!styleAttributes?.truck;
  const makeSlug = vehicle?.make?.slug;
  const year = vehicle?.year;
  const showExcoGenesis = isAds12269Enabled && makeSlug === 'genesis';

  const params = useMemo(
    () =>
      cloneParamsWithPubState({
        params: {
          ...propsParams,
          isElectric,
          isPluginHybrid,
          isTruck,
          isDealerlessOem: DEALERLESS_OEM.includes(makeSlug),
        },
        year,
      }),
    [isElectric, isPluginHybrid, isTruck, makeSlug, propsParams, year]
  );

  const edmundsTypeCategories = get(vehicle, 'edmundsTypeCategories');
  const hasTeaser = !!get(firstContent, 'hasTeaser');
  const hasEditorialReview = !!editorialReview;
  const overallRating = getOverallRating(editorialReview);
  // content tracking
  useEffect(() => {
    // edmundsTypeCategories
    if (!isTracked.current.edmundsTypeCategories && edmundsTypeCategories) {
      isTracked.current.edmundsTypeCategories = true;

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          ...CONTENT_TRACKING_DATA,
          subaction_name: TrackingConstant.VEHICLE_TYPE,
          value: Object.keys(edmundsTypeCategories).toString(),
        },
      });
    }

    // teaser
    if (!isTracked.current.teaser && hasTeaser) {
      isTracked.current.teaser = true;

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          ...CONTENT_TRACKING_DATA,
          subaction_name: TrackingConstant.SHOW_TEASER,
          value: 'yes',
        },
      });
    }

    // editorial rating
    if (!isTracked.current.editorialRating && hasEditorialReview) {
      isTracked.current.editorialRating = true;

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          ...CONTENT_TRACKING_DATA,
          subaction_name: TrackingConstant.RATING_CATEGORY,
          value: overallRating ? getEditorialIcon(overallRating).text : 'no rating',
        },
      });
    }
  }, [hasEditorialReview, overallRating, hasTeaser, edmundsTypeCategories]);

  if (!(vehicle && vehicle.make && vehicle.model && vehicle.submodels && vehicle.year && vehicle.pubStates)) {
    return (
      <main id="main-content" tabIndex={-1} className="core-page loading">
        <Container>Loading&hellip;</Container>
      </main>
    );
  }

  const displayVehicle = buildDisplayVehicle({
    makeModelSubmodelYear: vehicle,
    params,
    disableTrademarkCharacter: true,
  });
  const {
    make: { name: makeName },
    model: { slug: modelSlug, name: modelName },
    submodels: { slug: submodelSlug },
    modelYearId,
    pubStates,
  } = vehicle;

  const isPreprod = isPreProd(pubStates);

  const isInternational = isUnknownZip(visitorLocation);
  const noEditorialReview =
    !(get(editorialReview, 'reviewLink') || get(editorialReview, 'hasRelatedArticles')) &&
    !get(firstContent, 'articles.length');

  const headerClassName = 'header-background pos-a top-0 left-0 w-100';

  const typeBreadcrumb = getBodyTypeBreadcrumbFromCategories(edmundsTypeCategories);

  const core7007Recipe = isPreprod ? 'ctrl' : core7007;
  const core7705Recipe = isPreprod ? 'ctrl' : core7705;

  const OtherReviewYearsComponent = isCore7661Enabled ? OtherReviewYearsGraphql : OtherReviewYears;

  const coreEtrActivationEnabled = !isPreprod && isCoreEtrActivationEnabled(makeSlug, modelSlug, year, submodelSlug);
  const coreEtrActivationRecipe = coreEtrActivationEnabled ? coreEtrActivation : 'ctrl';

  let headerSrc;
  switch (true) {
    case core7705Recipe === 'chal2' && !isMobile: {
      headerSrc = CORE_DESKTOP_HEADER_CORE_7705;
      break;
    }
    default: {
      headerSrc = CORE_HEADER_SRC_INLINE[isMobile];
    }
  }

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className={classnames('core-page pos-r pt-0_75 pt-md-1_5', {
        'core-etr-activation': coreEtrActivationRecipe.includes('chal'),
        [coreEtrActivationRecipe]: coreEtrActivationRecipe.includes('chal'),
      })}
    >
      {coreEtrActivationRecipe.includes('chal') ? (
        <ClippedContainer containerClasses={headerClassName} mask={CORE_HEADER_ETR_ACTIVATION[isMobile]} />
      ) : (
        <img
          className={classnames(headerClassName, {
            'core-7705-chal2': core7705Recipe === 'chal2',
          })}
          src={headerSrc}
          alt=""
          fetchPriority="high"
        />
      )}

      <NewCoreIntro
        params={{ ...params, noEditorialReview }}
        vehicle={vehicle}
        editorialReview={editorialReview}
        firstContent={firstContent}
        visitorLocation={visitorLocation}
        inventoryCount={inventoryCount}
        isMobile={isMobile}
        isPreprod={isPreprod}
        isInternational={isInternational}
        core7007Recipe={core7007Recipe}
        ads11455Ads11617Recipe={ads11455Ads11617Recipe}
        isCore7661Enabled={isCore7661Enabled}
        core7705Recipe={core7705Recipe}
        isAds12269Enabled={showExcoGenesis}
        isCore7747Enabled={isPreprod && isCore7747}
        isCore7591Enabled={params.isCore7591Enabled}
        coreEtrActivationRecipe={coreEtrActivationRecipe}
      />
      <Container>
        <Row>
          {!isPreprod && (
            <Col xs={12} lg={8}>
              <ScrollElement id={MENU_HASH.CONSUMER_REVIEWS}>
                <ConsumerReviewsModular
                  className="mb-3_5"
                  vehicle={vehicle}
                  isMobile={isMobile}
                  creativeId="consumer-reviews"
                  withReviewsSummary
                />
              </ScrollElement>
            </Col>
          )}
          <Col xs={12} lg={isPreprod ? undefined : 4}>
            {!isPreprod && (
              <ShoppingLinks
                className="mb-2 mb-md-0"
                params={params}
                vehicle={vehicle}
                inventoryTotalNumber={inventoryCount?.NEW}
                isCore7007Enabled={core7007Recipe.includes('chal')}
              />
            )}

            {isMobile && (
              <Container>
                <Row className="build-price-viewable-parent">
                  <Col xs={12}>
                    <BuildPriceAd
                      mobile={isMobile}
                      wrapperClass={classnames('core', { 'd-flex justify-content-center': isMobile })}
                      includeMrectAdCall
                      position={'0'}
                    />
                  </Col>
                </Row>
              </Container>
            )}

            {!isMobile && (
              <div className="my-2 adsol-mrect">
                <NativeVideoMrect
                  position={'1'}
                  wiredOnly
                  wiredBreakpoints={{ lg: true, xl: true }}
                  refreshable={false}
                />
              </div>
            )}
          </Col>
        </Row>
      </Container>
      <Container>
        <ScrollElement id={MENU_HASH.VIDEOS}>
          <LatestVideoForGenerationModule
            className="border-0"
            isMobile={isMobile}
            vehicle={vehicle}
            videoLargeColumnSize={8}
            playlistLargeColumnSize={4}
            pageName={`${getPublicationStateForMakeModel(vehicle.pubStates)}_model_core`}
            withSchemaMeta={false}
            lazyLoadPreview
            isModularDesign
          />
        </ScrollElement>
        <hr className="mt-2 mb-3_5" />
      </Container>
      {!isPreprod && (
        <Fragment>
          <ScrollElement tag={Container} id={MENU_HASH.FEATURES}>
            <Row>
              <Col xs={12} lg={8}>
                <FeaturesHighlights params={params} vehicle={vehicle} isMobile={isMobile} />
              </Col>
            </Row>
            <hr className="mt-2 mb-3_5" />
            <Row>
              <Col xs={12} lg={8}>
                <Reliability
                  className="mb-3_5"
                  vehicleParams={{ makeName, modelName, makeSlug, modelSlug, submodelSlug, year, modelYearId }}
                  isMobile={isMobile}
                />
              </Col>
            </Row>
          </ScrollElement>
          <ScrollElement tag={Container} id={MENU_HASH.COMPETITION}>
            <Row>
              <Col xs={12} lg={8}>
                <VehicleComparisons vehicle={vehicle} isMobile={isMobile} />
                <hr className="mt-2 mb-3_5" />
              </Col>
            </Row>
          </ScrollElement>
          {!params.isCore7591Enabled && (
            <ScrollElement tag={Container} className="mb-3_5" id={MENU_HASH.PRICE_CHECKER}>
              <MinHeightWrapper componentType="PRICE_CHECKER">
                <PriceCheckerPromo
                  vehicle={vehicle}
                  utmCampaign="lower-price-checker"
                  isMobile={isMobile}
                  isInternational={isInternational}
                  isMycoNewCore={params.isMycoNewCore}
                />
              </MinHeightWrapper>
            </ScrollElement>
          )}
          <Container>
            <Row>
              <Col xs={12} className="mb-3_5 mb-lg-0">
                <NativeVideoMrect
                  position={'1'}
                  mobileBreakpoints={{ xs: true, sm: true }}
                  wiredBreakpoints={{ md: true }}
                  refreshable={false}
                />
              </Col>
            </Row>
          </Container>

          <ScrollElement tag={Container} id={MENU_HASH.SAFETY}>
            <Row>
              <Col xs={12} lg={8}>
                <Safety modelName={modelName} editorialReview={editorialReview} params={params} hasSeparator={false} />
                <hr className="mt-2 mb-3_5" />
                {isMobile && (
                  <section className="mb-3_5" data-tracking-parent="interior-photos">
                    <h2 className="heading-3 mb-1">Interior Photos</h2>
                    <PhotoGallery
                      params={params}
                      vehicle={vehicle}
                      editorialReview={editorialReview}
                      isPreprod={isPreprod}
                      hideHeroPhoto={isMobile}
                      isMobile={isMobile}
                      isCore6981Test={!isPreprod}
                    />
                  </section>
                )}
              </Col>

              <Col xs={12} lg={4}>
                <Container className="my-md-0_25 pb-md-1_5 mrect adsol-mrect">
                  <MrectNative
                    position={'2'}
                    wiredOnly
                    wiredBreakpoints={{ lg: true, xl: true }}
                    refreshable={false}
                    addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
                  />
                </Container>
              </Col>
            </Row>
          </ScrollElement>
        </Fragment>
      )}
      <Container>
        <Row>
          <Col xs={12} lg={8}>
            <VehicleQuestions params={params} />
            {!isPreprod && (
              <Fragment>
                <OtherReviewYearsComponent
                  className="bg-cool-gray-90 rounded-12 px-1 py-1 mb-3_5"
                  heading={`Research other ${modelName} years`}
                  headingClassName="mb-0_75"
                  vehicle={vehicle}
                  isMobile={isMobile}
                  hasSpeculationRule
                />
                <ScrollElement id={MENU_HASH.FAQS}>
                  <FaqSection vehicle={vehicle} params={params} />
                </ScrollElement>
                <hr className="mt-1 mb-3_5" />
                {!(isElectric || isPluginHybrid) && (
                  <RangeAndCost params={params} vehicle={vehicle} className="mb-1_5" isMobile={isMobile} />
                )}
              </Fragment>
            )}
          </Col>
        </Row>
      </Container>
      <Container>
        <CoreBreadcrumbs
          className="ps-0 mb-2"
          vehicle={vehicle}
          breadcrumbsParams={{
            params: propsParams,
            isMainCorePage: true,
            includeLastLink: true,
            typeBreadcrumb,
          }}
          creativeId="breadcrumb"
        />
        <PageLinksWidget divideSections={false} title={`Related ${displayVehicle} info`} className="mb-3_5" />
      </Container>
      {!isPreprod && isMobile && (
        <DockedSrpLink
          params={params}
          showElementSelector="[data-tracking-parent='edm-entry-review-scorecard2'],.core-also-viewed-vehicles"
          inventoryTotalNumber={inventoryCount?.total}
          trackingId="docked_srp_button"
          creativeId="new_core_dock_bottom"
          makeName={makeName}
          modelName={modelName}
          core7007Recipe={core7007Recipe}
        />
      )}
      {params.isPluginHybrid && (
        <ClientSideRender>
          <AppointmentSurveyExperiment experimentName="core-5931-siteintercept" />
        </ClientSideRender>
      )}
      {!isPreprod && (
        <Experiment name="core-7007-safs-seo" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal1" />
          <Recipe name="chal2" />
        </Experiment>
      )}
      {!isPreprod && (
        <Experiment name="ads-11455" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal1" />
          <Recipe name="chal2" />
        </Experiment>
      )}
      {!isPreprod && (
        <Experiment name="core-7705-fullnav" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal1" />
          <Recipe name="chal2" />
        </Experiment>
      )}
      {params.isCore7591Enabled && (
        <Experiment name="core-7591-used" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal" />
        </Experiment>
      )}
      {coreEtrActivationEnabled && (
        <Experiment name="core-etr-activation2" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal1" />
          <Recipe name="chal2" />
        </Experiment>
      )}
      {isPreprod && (
        <Experiment name="core-7747-pphdrs" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal" />
        </Experiment>
      )}
    </main>
  );
}

NewCoreUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  editorialReview: EditorialReviewEntities.EditorialReview,
  firstContent: FirstContentEntity,
  visitorLocation: VisitorEntities.Location,
  stylesFeatures: PropTypes.arrayOf(FeatureSpecsEntities.FeatureStyleEntity),
  sharedTrimName: PropTypes.string,
  isMobile: PropTypes.bool,
  inventoryCount: InventoryAggregateEntities.InventoryCountByType,
  core7007: PropTypes.string,
  ads11455Ads11617Recipe: PropTypes.string,
  isCore7661Enabled: PropTypes.bool,
  core7705: PropTypes.string,
  isCore7747: PropTypes.bool,
  isAds12269Enabled: PropTypes.bool,
  coreEtrActivation: PropTypes.string,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  core7007: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'core-7007-safs-seo',
    defaultVal: 'ctrl',
  }),
  ads11455Ads11617Recipe: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'ads-11455',
    defaultVal: 'ctrl',
  }),
  core7705: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'core-7705-fullnav',
    defaultVal: 'ctrl',
  }),
  isCore7661Enabled: !!state.featureFlags?.['core-7661-gql-vehicle'],
  isAds12269Enabled: !!state.featureFlags?.['ads-12269-exco-genesis'],
  isCore7747:
    ExperimentUtil.getForcedOrAssignedRecipeName({
      state,
      campaignName: 'core-7747-pphdrs',
      defaultVal: 'ctrl',
    }) === 'chal',
  coreEtrActivation: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'core-etr-activation2',
    defaultVal: 'ctrl',
  }),
});

/*
 * Please keep these binds to a minimum and only to page-level binds.
 * Component-level binds (binds needed by components) should be done
 * within the components themselves. This also ensures components are
 * completely standalone and reusable.
 */
export const NewCore = connect(mapStateToProps)(
  connectToModel(NewCoreUI, {
    visitorLocation: bindToPath('location', VisitorModel),
    editorialReview: bindToPath(
      ({ vehicle }) => vehicle && buildReviewPath(getParamsFromVehicle(vehicle)),
      EditorialReviewModel
    ),
    firstContent: bindToPath(
      ({ vehicle }) => vehicle && buildPreProdContentPath(getParamsFromVehicle(vehicle)),
      EditorialReviewModel
    ),
    stylesFeatures: bindToPath(
      ({ vehicle }) => vehicle && FeatureSpecsPaths.buildPartialFeaturesPath(getParamsFromVehicle(vehicle)),
      VehicleModel
    ),
    sharedTrimName: bindToPath('sharedValues.trimName', PageModel),
    inventoryCount: bindToPath(
      ({ vehicle }) =>
        vehicle &&
        buildInventoryCountByTypePath({
          ...getParamsFromVehicle(vehicle),
          year: isPreProd(vehicle.pubStates) || DEALERLESS_OEM.includes(vehicle.make.slug) ? undefined : vehicle.year,
        }),
      InventoryAggregateModel
    ),
  })
);

NewCore.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
};
